import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/contexts/AuthContext';
import api from '@/lib/api';
import { resizeImageForAvatar } from '@/lib/imageResize';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useToast } from '@/hooks/use-toast';
import MultiSelectChips from '@/components/MultiSelectChips';
import { NIGERIA_REGIONS, statesForRegions, COUNTRIES_WITH_REGIONS } from '@/data/nigeriaRegions';
import { ArrowLeft, User, Phone, Building, Camera, Save, Trash2, Loader2 } from 'lucide-react';

// Same list of countries the register page offers under Distributor coverage;
// keep them in sync when we add more with region data.
const COVERAGE_COUNTRIES = [
  { code: 'NG', name: 'Nigeria' },
  { code: 'KE', name: 'Kenya' },
  { code: 'GH', name: 'Ghana' },
  { code: 'ZA', name: 'South Africa' },
  { code: 'UG', name: 'Uganda' },
  { code: 'TZ', name: 'Tanzania' },
];

const BUSINESS_FIELDS = {
  manufacturer: { key: 'company_name', labelKey: 'editProfile.businessLabels.manufacturer' },
  distributor: { key: 'business_name', labelKey: 'editProfile.businessLabels.distributor' },
  shopkeeper: { key: 'shop_name', labelKey: 'editProfile.businessLabels.shopkeeper' },
};

export default function EditProfilePage() {
  const navigate = useNavigate();
  const { user, updateUser } = useAuth();
  const { toast } = useToast();
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);

  // Coverage is stashed under user.data.coverage — same shape the register
  // flow persists. Default missing pieces so the pickers stay controlled.
  const savedCoverage = user?.data?.coverage || {};
  const [formData, setFormData] = useState({
    full_name: user?.full_name || '',
    phone: user?.phone || '',
    company_name: user?.company_name || '',
    business_name: user?.business_name || '',
    shop_name: user?.shop_name || '',
    location: user?.location || '',
    email: user?.email || '',
    country: savedCoverage.country || 'NG',
    regions: Array.isArray(savedCoverage.regions) ? savedCoverage.regions : [],
    states: Array.isArray(savedCoverage.states) ? savedCoverage.states : [],
  });
  // Avatar lives outside the main formData so touching other fields
  // doesn't invalidate the preview. `undefined` means "not changed",
  // a string means "set to this", empty string means "clear it".
  const [avatarPreview, setAvatarPreview] = useState(user?.avatar_url || '');
  const [avatarDirty, setAvatarDirty] = useState(false);
  const [avatarProcessing, setAvatarProcessing] = useState(false);
  const avatarInputRef = useRef(null);

  const isDistributor = user?.user_type === 'distributor';
  const countryHasRegions = COUNTRIES_WITH_REGIONS.has(formData.country);
  const availableStateOptions = statesForRegions(formData.regions).map((s) => ({ value: s, label: s }));

  // Keep the states list valid: if the user removes a region, drop states
  // that no longer belong to any selected region.
  useEffect(() => {
    const allowed = new Set(statesForRegions(formData.regions));
    setFormData((prev) => {
      const filtered = prev.states.filter((s) => allowed.has(s));
      return filtered.length === prev.states.length ? prev : { ...prev, states: filtered };
    });
  }, [formData.regions]);

  const businessField = BUSINESS_FIELDS[user?.user_type];
  const businessLabel = businessField ? t(businessField.labelKey) : '';

  const handleAvatarPick = async (e) => {
    const file = e.target?.files?.[0];
    if (!file) return;
    // Guard against the pathological "picked a 20 MB raw photo" case
    // BEFORE calling into canvas — the resizer handles it, but it's
    // faster to reject at the file picker than to decode 20 MB first.
    if (file.size > 10 * 1024 * 1024) {
      toast({ title: t('editProfile.avatar.tooBig', { defaultValue: 'That photo is too large. Pick one under 10 MB.' }), variant: 'destructive' });
      e.target.value = '';
      return;
    }
    setAvatarProcessing(true);
    try {
      const dataUrl = await resizeImageForAvatar(file, { edge: 400, quality: 0.85 });
      setAvatarPreview(dataUrl);
      setAvatarDirty(true);
    } catch (err) {
      console.error('Avatar resize failed', err);
      toast({ title: t('editProfile.avatar.failed', { defaultValue: "Couldn't process that photo. Try another one." }), variant: 'destructive' });
    } finally {
      setAvatarProcessing(false);
      // Allow re-picking the SAME file (otherwise onChange doesn't fire).
      if (e.target) e.target.value = '';
    }
  };

  const handleAvatarRemove = () => {
    setAvatarPreview('');
    setAvatarDirty(true);
  };

  const handleSave = async () => {
    setLoading(true);
    // Build the payload — the backend PATCH treats every field as optional
    // and only touches what we send. Distributor-only fields go through when
    // the current role is a distributor.
    const payload = {
      full_name: formData.full_name,
      company_name: formData.company_name,
      business_name: formData.business_name,
      shop_name: formData.shop_name,
      location: formData.location,
    };
    // Email is optional. Send only when it was actually set, so the
    // Pydantic shape validator doesn't reject an empty string.
    if (formData.email && formData.email.trim()) {
      payload.email = formData.email.trim();
    }
    // Only send the avatar when the user actually touched it. An
    // empty string here means "clear it"; a data URL means "replace
    // it"; omission leaves whatever's on the server alone.
    if (avatarDirty) {
      payload.avatar_url = avatarPreview;
    }
    if (isDistributor) {
      payload.country = formData.country;
      payload.regions = formData.regions;
      payload.states = formData.states;
    }
    try {
      const res = await api.patch(`/users/${user.id}`, payload);
      // Prefer the server's canonical user; fall back to a local merge if the
      // response is shaped differently on some routes.
      const updatedUser = res?.data && res.data.id ? res.data : {
        ...user,
        ...payload,
        data: {
          ...(user?.data || {}),
          coverage: isDistributor
            ? { country: formData.country, regions: formData.regions, states: formData.states }
            : (user?.data?.coverage),
        },
      };
      localStorage.setItem('user', JSON.stringify(updatedUser));
      updateUser?.(updatedUser);
      toast({ title: t('editProfile.toast.updated') });
      navigate('/profile');
    } catch (error) {
      // Backend may be offline — still persist locally so the user sees
      // their edit and gets a hint that it hasn't synced yet.
      console.warn('Profile PATCH failed, saving locally', error);
      const updatedUser = {
        ...user,
        ...payload,
        data: {
          ...(user?.data || {}),
          coverage: isDistributor
            ? { country: formData.country, regions: formData.regions, states: formData.states }
            : (user?.data?.coverage),
        },
      };
      localStorage.setItem('user', JSON.stringify(updatedUser));
      updateUser?.(updatedUser);
      toast({ title: t('editProfile.toast.updatedLocal') });
      navigate('/profile');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-4 animate-fadeIn">
      {/* Header */}
      <div className="flex items-center gap-4 mb-6">
        <Button variant="ghost" size="icon" onClick={() => navigate(-1)}>
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <h1 className="font-heading text-2xl font-bold">{t('editProfile.title')}</h1>
      </div>

      <Card>
        <CardContent className="p-6 space-y-6">
          {/* Avatar — tap the circle to pick a photo. The resizer
              center-crops to a 400 px square JPEG so the stored blob
              stays tiny and the UI never has to deal with aspect-
              ratio surprises. */}
          <div className="flex flex-col items-center gap-3">
            <button
              type="button"
              onClick={() => avatarInputRef.current?.click()}
              className="relative w-24 h-24 bg-farm-primary/10 rounded-full flex items-center justify-center overflow-hidden focus:outline-none focus:ring-2 focus:ring-farm-primary focus:ring-offset-2 group"
              aria-label={t('editProfile.avatar.changeAria', { defaultValue: 'Change profile photo' })}
              data-testid="avatar-picker-btn"
            >
              {avatarPreview ? (
                <img src={avatarPreview} alt="" className="w-full h-full object-cover" />
              ) : (
                <User className="w-12 h-12 text-farm-primary" />
              )}
              <span className="absolute inset-x-0 bottom-0 bg-black/55 text-white text-[10px] font-semibold py-1 flex items-center justify-center gap-1 opacity-0 group-hover:opacity-100 group-focus:opacity-100 transition-opacity">
                {avatarProcessing ? (
                  <Loader2 className="w-3 h-3 animate-spin" />
                ) : (
                  <>
                    <Camera className="w-3 h-3" />
                    {t('editProfile.avatar.change', { defaultValue: 'Change' })}
                  </>
                )}
              </span>
            </button>
            <input
              ref={avatarInputRef}
              type="file"
              accept="image/jpeg,image/png"
              onChange={handleAvatarPick}
              className="hidden"
              data-testid="avatar-file-input"
            />
            {avatarPreview && (
              <button
                type="button"
                onClick={handleAvatarRemove}
                className="text-xs text-red-600 inline-flex items-center gap-1 hover:underline"
                data-testid="avatar-remove-btn"
              >
                <Trash2 className="w-3 h-3" />
                {t('editProfile.avatar.remove', { defaultValue: 'Remove photo' })}
              </button>
            )}
          </div>

          {/* Full Name */}
          <div className="space-y-2">
            <Label htmlFor="full_name">{t('editProfile.fullNameLabel')}</Label>
            <div className="relative">
              <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input
                id="full_name"
                value={formData.full_name}
                onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                className="pl-10"
                placeholder={t('editProfile.fullNamePlaceholder')}
              />
            </div>
          </div>

          {/* Phone */}
          <div className="space-y-2">
            <Label htmlFor="phone">{t('editProfile.phoneLabel')}</Label>
            <div className="relative">
              <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input
                id="phone"
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                className="pl-10"
                placeholder={t('editProfile.phonePlaceholder')}
                disabled
              />
            </div>
            <p className="text-xs text-muted-foreground">{t('editProfile.phoneCannotChange')}</p>
          </div>

          {/* Business Name (role-specific) */}
          {businessField && (
            <div className="space-y-2">
              <Label htmlFor={businessField.key}>{businessLabel}</Label>
              <div className="relative">
                <Building className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  id={businessField.key}
                  value={formData[businessField.key]}
                  onChange={(e) => setFormData({ ...formData, [businessField.key]: e.target.value })}
                  className="pl-10"
                  placeholder={t('editProfile.businessPlaceholder', { label: businessLabel.toLowerCase() })}
                />
              </div>
            </div>
          )}

          {/* Location */}
          <div className="space-y-2">
            <Label htmlFor="location">{t('editProfile.locationLabel')}</Label>
            <Input
              id="location"
              value={formData.location}
              onChange={(e) => setFormData({ ...formData, location: e.target.value })}
              placeholder={t('editProfile.locationPlaceholder')}
            />
          </div>

          {/* Email — optional. Used for Stripe receipts and future
              notifications. Signup is phone-only, so this is where
              most users add an inbox. Backend validates shape. */}
          <div className="space-y-2">
            <Label htmlFor="profile-email">{t('editProfile.emailLabel', { defaultValue: 'Email (optional)' })}</Label>
            <Input
              id="profile-email"
              type="email"
              autoComplete="email"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              placeholder={t('editProfile.emailPlaceholder', { defaultValue: 'you@example.com' })}
              data-testid="profile-email-input"
            />
            <p className="text-xs text-muted-foreground">
              {t('editProfile.emailHint', {
                defaultValue: 'Used for subscription receipts and important notifications.',
              })}
            </p>
          </div>

          {/* Distributor coverage — country + regions + states. Same three
              fields the register flow captures, so the distributor can
              amend them post-signup from one place. */}
          {isDistributor && (
            <div className="space-y-4 border-t pt-4">
              <div>
                <Label className="mb-2 block">{t('auth.register.coverage.country')}</Label>
                <Select
                  value={formData.country}
                  onValueChange={(v) => setFormData({ ...formData, country: v })}
                >
                  <SelectTrigger data-testid="edit-country">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {COVERAGE_COUNTRIES.map((c) => (
                      <SelectItem key={c.code} value={c.code}>{c.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {countryHasRegions ? (
                <>
                  <div>
                    <Label className="mb-2 block">{t('auth.register.coverage.regions')}</Label>
                    <MultiSelectChips
                      options={NIGERIA_REGIONS.map((r) => ({ value: r.id, label: t(r.labelKey) }))}
                      value={formData.regions}
                      onChange={(next) => setFormData({ ...formData, regions: next })}
                      placeholder={t('auth.register.coverage.regionsPlaceholder')}
                      searchPlaceholder={t('auth.register.coverage.regionsSearch')}
                      emptyText={t('auth.register.coverage.regionsEmpty')}
                      testId="edit-regions"
                    />
                  </div>
                  <div>
                    <Label className="mb-2 block">{t('auth.register.coverage.states')}</Label>
                    <MultiSelectChips
                      options={availableStateOptions}
                      value={formData.states}
                      onChange={(next) => setFormData({ ...formData, states: next })}
                      placeholder={
                        formData.regions.length === 0
                          ? t('auth.register.coverage.statesPickRegionFirst')
                          : t('auth.register.coverage.statesPlaceholder')
                      }
                      searchPlaceholder={t('auth.register.coverage.statesSearch')}
                      emptyText={t('auth.register.coverage.statesEmpty')}
                      disabled={formData.regions.length === 0}
                      testId="edit-states"
                    />
                  </div>
                </>
              ) : (
                <p className="text-xs text-muted-foreground">
                  {t('auth.register.coverage.noRegionData')}
                </p>
              )}
            </div>
          )}

          {/* Save Button */}
          <Button
            className="w-full bg-farm-primary hover:bg-farm-primary-dark"
            onClick={handleSave}
            disabled={loading}
          >
            {loading ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Save className="w-4 h-4 mr-2" />
            )}
            {t('editProfile.save')}
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}
